import type { GisSummary, MandalAggregate } from '../api/types'

/**
 * District **Health** = the plain average of the three bars the Overview hero actually shows
 * (coverage, officer completion, issue-control). No hidden weights — the number reconciles to the
 * bars by construction (V2 decision D3), unlike the server `healthScore` we no longer headline.
 */

/** Issue-control = how free of critical issues the district is. `null` share = full control. */
export function issueControl(criticalSharePct: number | null): number {
  return 100 - Math.min(100, criticalSharePct ?? 0)
}

/**
 * The transparent 0–100 health = average of the three shown bars. Null coverage/officers count as 0
 * (empty scope). Shared by district and mandal so the two scopes reconcile by construction.
 */
export function healthFrom(coverage: number | null, officerAvgCompletion: number | null, criticalSharePct: number | null): number {
  return Math.round(((coverage ?? 0) + (officerAvgCompletion ?? 0) + issueControl(criticalSharePct)) / 3)
}

/** District transparent health, from the summary row. */
export function districtHealth(summary: GisSummary): number {
  return healthFrom(summary.coverage, summary.officerAvgCompletion, summary.criticalSharePct)
}

/** Mandal transparent health, from a single mandal row — same formula as {@link districtHealth}. */
export function mandalHealth(m: MandalAggregate): number {
  return healthFrom(m.coverage, m.officerAvgCompletion, m.criticalSharePct)
}

export type VerdictTone = 'good' | 'warn' | 'bad'
export interface Verdict {
  tone: VerdictTone
  label: string
}

/** Operating-health verdict bands: ≥75 Healthy · ≥55 Holding · else Needs action. */
export function healthVerdict(score: number): Verdict {
  if (score >= 75) {
    return { tone: 'good', label: 'Healthy' }
  }
  if (score >= 55) {
    return { tone: 'warn', label: 'Holding' }
  }
  return { tone: 'bad', label: 'Needs action' }
}

const coveragePhrase = (cd: number | null): string => {
  if (cd !== null && cd > 1) {
    return 'Coverage is improving'
  }
  if (cd !== null && cd < -1) {
    return 'Coverage is slipping'
  }
  return 'Coverage is holding'
}

const issuePhrase = (od: number | null): string => {
  if (od !== null && od > 1) {
    return ', but active issues are rising'
  }
  if (od !== null && od < -1) {
    return ', and issues are easing'
  }
  return ''
}

const officerPhrase = (below: number): string =>
  below > 0 ? ` ${below} officer${below === 1 ? ' is' : 's are'} below 60% completion.` : ''

/**
 * The plain-language "what's wrong" line under the Overview health hero — generated from the SAME
 * period deltas + below-60 count the movement cards use, so the prose can never contradict them.
 */
export function overviewProse(summary: GisSummary): string {
  return `${coveragePhrase(summary.deltas.coverage)}${issuePhrase(summary.deltas.activeIssues)}.${officerPhrase(summary.officersBelow60Count)}`
}

export interface MovementVerdict extends Verdict {
  /** ↑ / ↓ / → for the period-over-period direction. */
  arrow: '↑' | '↓' | '→'
}

/**
 * Plain-language movement read for a period delta. `|delta| < 2` reads "Holding" (noise floor);
 * otherwise Improving/Regressing depending on whether up is good for this metric (`goodUp` is false
 * for metrics like active issues where rising is bad). Null delta (no prior window) holds.
 */
export function movementVerdict(delta: number | null, goodUp: boolean): MovementVerdict {
  if (delta === null || Math.abs(delta) < 2) {
    return { tone: 'warn', label: 'Holding', arrow: delta === null || delta === 0 ? '→' : delta > 0 ? '↑' : '↓' }
  }
  const up = delta > 0
  const good = goodUp ? up : !up
  return { tone: good ? 'good' : 'bad', label: good ? 'Improving' : 'Regressing', arrow: up ? '↑' : '↓' }
}
